// ────────────────────────────────────────────────────────────────
// Portail Client B2B — Authentification email + mot de passe
// Vues : login · activation · forgot · reset · dashboard
// ────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import { PUBLISHER_LABEL, COPYRIGHT_YEAR } from "../config/legal";
import { Eye, EyeOff, Lock, Mail, AlertCircle, CheckCircle, RotateCcw, FileText, Package, User, LogOut } from "lucide-react";

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("fr-FR"); }

const STATUS_LABEL = { paid: "Payée", pending: "En attente", late: "En retard", draft: "Brouillon", cancelled: "Annulée" };
const STATUS_COLOR = { paid: "#059669", pending: "#D97706", late: "#DC2626", draft: "#6B7280", cancelled: "#9CA3AF" };
const STATUS_BG    = { paid: "#D1FAE5", pending: "#FEF3C7", late: "#FEE2E2", draft: "#F3F4F6", cancelled: "#F9FAFB" };

const MDP_HINT = 'Min. 8 caractères · 1 majuscule · 1 chiffre · 1 caractère spécial';

// Validation MDP côté client (miroir du regex backend)
const PASSWORD_RE = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?]).{8,128}$/;

// Lire le paramètre d'action dans l'URL (?action=activate&token=...)
function parseUrlAction() {
  const params = new URLSearchParams(window.location.search);
  return { action: params.get('action'), token: params.get('token') };
}

// ── Styles partagés ───────────────────────────────────────────────────────────
const s = {
  page:   { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#0d1117 0%,#1a2035 50%,#0f2519 100%)" },
  card:   { background: "#fff", borderRadius: 16, padding: "36px 40px", maxWidth: 440, width: "90%", boxShadow: "0 24px 64px rgba(0,0,0,0.3)" },
  label:  { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" },
  input:  { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid #D1D5DB", fontSize: 14, outline: "none", boxSizing: "border-box" },
  btn:    { width: "100%", padding: "12px", borderRadius: 10, border: "none", background: "#059669", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" },
  btnSec: { background: "none", border: "none", color: "#059669", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  err:    { background: "#FEF2F2", color: "#991B1B", border: "1px solid #FCA5A5", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start" },
  ok:     { background: "#ECFDF5", color: "#065F46", border: "1px solid #6EE7B7", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
  hint:   { fontSize: 11, color: "#9CA3AF", marginTop: 4 },
  footer: { fontSize: 11, color: "#9CA3AF", marginTop: 20, textAlign: "center" },
};

// ── Champ mot de passe avec oeil ──────────────────────────────────────────────
function PasswordInput({ value, onChange, placeholder = "Mot de passe", autoComplete = "current-password" }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{ ...s.input, paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={() => setShow(v => !v)}
        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#6B7280" }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

// ── Vue : Login email + mot de passe ─────────────────────────────────────────
function LoginView({ onLogin, onForgot }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) { setError("Email et mot de passe requis."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${BASE}/portal/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erreur de connexion");
      onLogin(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#059669,#047857)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Lock size={24} color="#fff" />
          </div>
          <div style={{ fontWeight: 800, fontSize: 20 }}>FinanceApp</div>
          <div style={{ color: "#6B7280", fontSize: 13, marginTop: 4 }}>Espace Client Sécurisé</div>
        </div>

        {error && (
          <div style={s.err}>
            <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={s.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="votre@email.com"
              autoComplete="email"
              required
              style={s.input}
            />
          </div>
          <div>
            <label style={s.label}>Mot de passe</label>
            <PasswordInput value={password} onChange={setPassword} placeholder="Votre mot de passe" autoComplete="current-password" />
          </div>
          <button type="submit" disabled={loading} style={{ ...s.btn, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Connexion…" : "Se connecter →"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button onClick={onForgot} style={s.btnSec}>Mot de passe oublié ?</button>
        </div>
        <div style={s.footer}>🔒 Accès sécurisé · Données chiffrées TLS · RGPD</div>
        <div style={{ ...s.footer, marginTop: 6 }}>© {COPYRIGHT_YEAR} {PUBLISHER_LABEL}</div>
      </div>
    </div>
  );
}

// ── Vue : Activation compte (lien d'invitation) ───────────────────────────────
function ActivateView({ token, onDone }) {
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState(false);

  const pwdOk    = PASSWORD_RE.test(password);
  const matchOk  = password && confirm && password === confirm;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!pwdOk)   { setError(MDP_HINT); return; }
    if (!matchOk) { setError("Les mots de passe ne correspondent pas."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${BASE}/portal/activate/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Activation échouée");
      setSuccess(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (success) return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: "center" }}>
        <CheckCircle size={48} color="#059669" style={{ marginBottom: 16 }} />
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Compte activé !</div>
        <p style={{ color: "#6B7280", fontSize: 14, marginBottom: 20 }}>Votre compte est prêt. Vous pouvez maintenant vous connecter avec votre email et mot de passe.</p>
        <button onClick={onDone} style={s.btn}>Se connecter →</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Lock size={22} color="#059669" />
          </div>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Activation de votre compte</div>
          <div style={{ color: "#6B7280", fontSize: 13, marginTop: 4 }}>Choisissez votre mot de passe pour activer l'accès</div>
        </div>

        {error && <div style={s.err}><AlertCircle size={14} style={{ flexShrink: 0 }} /> {error}</div>}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={s.label}>Nouveau mot de passe</label>
            <PasswordInput value={password} onChange={setPassword} placeholder="Choisissez un mot de passe fort" autoComplete="new-password" />
            <div style={{ ...s.hint, color: pwdOk && password ? "#059669" : "#9CA3AF" }}>
              {MDP_HINT}
            </div>
          </div>
          <div>
            <label style={s.label}>Confirmer le mot de passe</label>
            <PasswordInput value={confirm} onChange={setConfirm} placeholder="Répétez le mot de passe" autoComplete="new-password" />
            {confirm && !matchOk && <div style={{ ...s.hint, color: "#DC2626" }}>Les mots de passe ne correspondent pas.</div>}
            {confirm && matchOk  && <div style={{ ...s.hint, color: "#059669" }}>✓ Mots de passe identiques</div>}
          </div>
          <button type="submit" disabled={loading || !pwdOk || !matchOk} style={{ ...s.btn, opacity: (!pwdOk || !matchOk) ? 0.5 : 1 }}>
            {loading ? "Activation…" : "Activer mon compte →"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Vue : Mot de passe oublié ─────────────────────────────────────────────────
function ForgotView({ onBack }) {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${BASE}/portal/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erreur");
      setSent(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (sent) return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: "center" }}>
        <Mail size={44} color="#059669" style={{ marginBottom: 14 }} />
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>Email envoyé</div>
        <p style={{ color: "#6B7280", fontSize: 14, marginBottom: 20 }}>
          Si cet email est enregistré, vous recevrez sous peu un lien de réinitialisation valable 1 heure.
        </p>
        <button onClick={onBack} style={s.btn}>Retour à la connexion</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.card}>
        <button onClick={onBack} style={{ ...s.btnSec, marginBottom: 16, display: "flex", alignItems: "center", gap: 4 }}>
          ← Retour
        </button>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Mot de passe oublié</div>
        <p style={{ color: "#6B7280", fontSize: 13, marginBottom: 20 }}>Entrez votre email pour recevoir un lien de réinitialisation.</p>

        {error && <div style={s.err}><AlertCircle size={14} /> {error}</div>}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={s.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="votre@email.com"
              required
              style={s.input}
            />
          </div>
          <button type="submit" disabled={loading} style={{ ...s.btn, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Envoi…" : "Envoyer le lien →"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Vue : Réinitialiser le mot de passe ───────────────────────────────────────
function ResetView({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState(false);

  const pwdOk   = PASSWORD_RE.test(password);
  const matchOk = password && confirm && password === confirm;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!pwdOk)   { setError(MDP_HINT); return; }
    if (!matchOk) { setError("Les mots de passe ne correspondent pas."); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${BASE}/portal/reset-password/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Réinitialisation échouée");
      setSuccess(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (success) return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: "center" }}>
        <CheckCircle size={48} color="#059669" style={{ marginBottom: 16 }} />
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Mot de passe réinitialisé</div>
        <p style={{ color: "#6B7280", fontSize: 14, marginBottom: 20 }}>Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
        <button onClick={onDone} style={s.btn}>Se connecter →</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <RotateCcw size={22} color="#059669" />
          </div>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Nouveau mot de passe</div>
          <div style={{ color: "#6B7280", fontSize: 13, marginTop: 4 }}>Choisissez un mot de passe sécurisé</div>
        </div>

        {error && <div style={s.err}><AlertCircle size={14} style={{ flexShrink: 0 }} /> {error}</div>}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={s.label}>Nouveau mot de passe</label>
            <PasswordInput value={password} onChange={setPassword} placeholder="Choisissez un mot de passe fort" autoComplete="new-password" />
            <div style={{ ...s.hint, color: pwdOk && password ? "#059669" : "#9CA3AF" }}>{MDP_HINT}</div>
          </div>
          <div>
            <label style={s.label}>Confirmer</label>
            <PasswordInput value={confirm} onChange={setConfirm} placeholder="Répétez le mot de passe" autoComplete="new-password" />
            {confirm && !matchOk && <div style={{ ...s.hint, color: "#DC2626" }}>Les mots de passe ne correspondent pas.</div>}
            {confirm && matchOk  && <div style={{ ...s.hint, color: "#059669" }}>✓ Identiques</div>}
          </div>
          <button type="submit" disabled={loading || !pwdOk || !matchOk} style={{ ...s.btn, opacity: (!pwdOk || !matchOk) ? 0.5 : 1 }}>
            {loading ? "Mise à jour…" : "Confirmer le nouveau mot de passe →"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Vue : Dashboard portail ───────────────────────────────────────────────────
function PortalDashboard({ session, onLogout }) {
  const { token, company, customer } = session;
  const [tab,          setTab]          = useState("factures");
  const [docs,         setDocs]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [yearFilter,   setYearFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/portal/documents`, { headers });
      if (!res.ok) { onLogout(); return; } // JWT expiré
      const data = await res.json();
      setDocs(data);
    } catch { setDocs({ factures: [], avoirs: [], bls: [], devis: [] }); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const years = (() => {
    if (!docs?.factures?.length) return [];
    return [...new Set(docs.factures.map(f => new Date(f.date).getFullYear()))].sort((a, b) => b - a);
  })();

  const filteredFact = (docs?.factures || []).filter(f => {
    if (yearFilter   !== "all" && new Date(f.date).getFullYear().toString() !== yearFilter) return false;
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    return true;
  });

  async function downloadPdf(invoiceId, number) {
    try {
      const res  = await fetch(`${BASE}/portal/invoice/${invoiceId}/pdf`, { headers });
      if (!res.ok) { alert("PDF temporairement indisponible."); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `facture-${number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Erreur lors du téléchargement."); }
  }

  const TABS = [
    { id: "factures", label: "Factures",          Icon: FileText, count: docs?.factures?.length },
    { id: "avoirs",   label: "Avoirs",             Icon: RotateCcw, count: docs?.avoirs?.length },
    { id: "bls",      label: "Bons de livraison",  Icon: Package,  count: docs?.bls?.length },
    { id: "compte",   label: "Mon compte",          Icon: User },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#059669,#047857)", padding: "14px 24px", color: "#fff", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Espace client — {company?.name}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>{customer?.name || customer?.email}</div>
        </div>
        <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", padding: "6px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
          <LogOut size={13} /> Déconnexion
        </button>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
        {/* KPIs */}
        {docs && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 24 }}>
            {[
              { label: "Factures",  value: docs.factures?.length || 0,  color: "#059669" },
              { label: "Impayées",  value: (docs.factures || []).filter(f => f.status === 'pending' || f.status === 'late').length, color: "#D97706" },
              { label: "Total dû",  value: fmt((docs.factures || []).filter(f => f.status !== 'paid' && f.status !== 'cancelled').reduce((s, f) => s + (f.ttc_amount || 0), 0)) + " €", color: "#DC2626" },
            ].map(k => (
              <div key={k.label} style={{ background: "#fff", borderRadius: 12, padding: "16px 18px", border: "1px solid #E5E7EB" }}>
                <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Onglets */}
        <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #E5E7EB", marginBottom: 20 }}>
          {TABS.map(({ id, label, Icon, count }) => (
            <button key={id} onClick={() => setTab(id)} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "9px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13,
              fontWeight: tab === id ? 700 : 400,
              color: tab === id ? "#059669" : "#6B7280",
              borderBottom: tab === id ? "2px solid #059669" : "2px solid transparent",
            }}>
              <Icon size={14} />
              {label}
              {count != null && <span style={{ fontSize: 11, background: "#F3F4F6", padding: "1px 6px", borderRadius: 10, color: "#6B7280" }}>{count}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "#9CA3AF" }}>Chargement…</div>
        ) : (
          <>
            {/* Factures */}
            {tab === "factures" && (
              <div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, background: "#fff" }}>
                    <option value="all">Tous statuts</option>
                    {["paid", "pending", "late"].map(s => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                  {years.length > 0 && (
                    <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
                      style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 13, background: "#fff" }}>
                      <option value="all">Toutes les années</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  )}
                </div>

                {filteredFact.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 60, color: "#9CA3AF", background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB" }}>
                    <FileText size={40} color="#D1D5DB" style={{ marginBottom: 12 }} />
                    <div>Aucune facture</div>
                  </div>
                ) : (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#F9FAFB", fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>
                          {["N°", "Date", "Échéance", "Montant TTC", "Statut", ""].map(h => (
                            <th key={h} style={{ padding: "10px 16px", textAlign: h === "Montant TTC" ? "right" : "left", fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFact.map(f => (
                          <tr key={f.id} style={{ borderTop: "1px solid #F3F4F6", fontSize: 13 }}>
                            <td style={{ padding: "11px 16px", fontWeight: 600 }}>{f.doc_number}</td>
                            <td style={{ padding: "11px 16px" }}>{fmtDate(f.date)}</td>
                            <td style={{ padding: "11px 16px", color: f.status === 'late' ? "#DC2626" : "inherit" }}>{fmtDate(f.due_date)}</td>
                            <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 700 }}>{fmt(f.ttc_amount)} €</td>
                            <td style={{ padding: "11px 16px" }}>
                              <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, fontWeight: 700, background: STATUS_BG[f.status] || "#F3F4F6", color: STATUS_COLOR[f.status] || "#6B7280" }}>
                                {STATUS_LABEL[f.status] || f.status}
                              </span>
                            </td>
                            <td style={{ padding: "11px 16px" }}>
                              <button onClick={() => downloadPdf(f.id, f.doc_number)}
                                style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", fontSize: 12, cursor: "pointer" }}>
                                ↓ PDF
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Avoirs */}
            {tab === "avoirs" && (
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "auto" }}>
                {!docs?.avoirs?.length ? (
                  <div style={{ textAlign: "center", padding: 60, color: "#9CA3AF" }}>
                    <RotateCcw size={36} color="#D1D5DB" style={{ marginBottom: 12 }} />
                    <div>Aucun avoir</div>
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F9FAFB", fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>
                        {["N°", "Date", "Montant TTC", "PDF"].map(h => (
                          <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {docs.avoirs.map(a => (
                        <tr key={a.id} style={{ borderTop: "1px solid #F3F4F6", fontSize: 13 }}>
                          <td style={{ padding: "11px 16px", fontWeight: 600 }}>{a.doc_number}</td>
                          <td style={{ padding: "11px 16px" }}>{fmtDate(a.date)}</td>
                          <td style={{ padding: "11px 16px", fontWeight: 700, color: "#DC2626" }}>-{fmt(a.ttc_amount)} €</td>
                          <td style={{ padding: "11px 16px" }}>
                            <button onClick={() => downloadPdf(a.id, a.doc_number)}
                              style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", fontSize: 12, cursor: "pointer" }}>
                              ↓ PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* BLs */}
            {tab === "bls" && (
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "auto" }}>
                {!docs?.bls?.length ? (
                  <div style={{ textAlign: "center", padding: 60, color: "#9CA3AF" }}>
                    <Package size={36} color="#D1D5DB" style={{ marginBottom: 12 }} />
                    <div>Aucun bon de livraison</div>
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F9FAFB", fontSize: 11, color: "#6B7280", textTransform: "uppercase" }}>
                        {["Référence", "Livraison", "Transporteur", "N° suivi", "Statut"].map(h => (
                          <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {docs.bls.map(bl => (
                        <tr key={bl.id} style={{ borderTop: "1px solid #F3F4F6", fontSize: 13 }}>
                          <td style={{ padding: "11px 16px", fontWeight: 600 }}>{bl.note_ref}</td>
                          <td style={{ padding: "11px 16px" }}>{fmtDate(bl.delivery_date)}</td>
                          <td style={{ padding: "11px 16px" }}>{bl.carrier || "—"}</td>
                          <td style={{ padding: "11px 16px", fontFamily: "monospace", fontSize: 12, color: "#1D4ED8" }}>{bl.tracking_number || "—"}</td>
                          <td style={{ padding: "11px 16px" }}>
                            <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "#D1FAE5", color: "#065F46", fontWeight: 600 }}>{bl.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Mon compte */}
            {tab === "compte" && (
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Mes informations</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
                  {[
                    { label: "Nom",       value: customer?.name     || "—" },
                    { label: "Email",     value: customer?.email    || "—" },
                    { label: "Téléphone", value: customer?.phone    || "—" },
                    { label: "Adresse",   value: customer?.address  ? `${customer.address}, ${customer.city}` : "—" },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "#F9FAFB", borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
                    </div>
                  ))}
                </div>
                {company && (
                  <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 10, padding: "14px 16px" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#065F46", marginBottom: 6 }}>Votre fournisseur</div>
                    <div style={{ fontSize: 13, color: "#047857", fontWeight: 600 }}>{company.name}</div>
                    {company.address && <div style={{ fontSize: 12, color: "#059669", marginTop: 2 }}>{company.address}, {company.city}</div>}
                    {company.email   && <div style={{ fontSize: 12, color: "#059669" }}>✉ {company.email}</div>}
                    {company.phone   && <div style={{ fontSize: 12, color: "#059669" }}>📞 {company.phone}</div>}
                  </div>
                )}
                <div style={{ marginTop: 16, fontSize: 11, color: "#9CA3AF", textAlign: "center" }}>
                  🔒 Données chiffrées TLS · RGPD conforme
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Composant racine ──────────────────────────────────────────────────────────
export default function PortalClient() {
  const [session, setSession] = useState(null);
  const [view,    setView]    = useState(() => {
    const { action } = parseUrlAction();
    if (action === 'activate') return 'activate';
    if (action === 'reset')    return 'reset';
    return 'login';
  });

  const { token: urlToken } = parseUrlAction();

  function handleLogin(data) {
    // Nettoyer l'URL après login (enlève ?action=... et ?token=...)
    window.history.replaceState({}, "", "/portal");
    setSession(data);
  }

  function handleLogout() {
    setSession(null);
    setView('login');
    window.history.replaceState({}, "", "/portal");
  }

  if (session) return <PortalDashboard session={session} onLogout={handleLogout} />;

  if (view === 'activate' && urlToken) {
    return <ActivateView token={urlToken} onDone={() => { window.history.replaceState({}, "", "/portal"); setView('login'); }} />;
  }

  if (view === 'reset' && urlToken) {
    return <ResetView token={urlToken} onDone={() => { window.history.replaceState({}, "", "/portal"); setView('login'); }} />;
  }

  if (view === 'forgot') {
    return <ForgotView onBack={() => setView('login')} />;
  }

  return <LoginView onLogin={handleLogin} onForgot={() => setView('forgot')} />;
}
